import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod/v4";
import ky from "ky";
import type { HTTPError } from "ky";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/passwordInput";
import { env } from "@/env";
import { parseBackendError, parseRefusal } from "@/lib/utils/backend";
import { newPassword, requiredEmail, requiredString } from "@/lib/utils/zod";

const searchSchema = z.object({ email: z.string().optional() });
const schema = z.object({
  full_name: requiredString("Name is required"),
  email: requiredEmail("Enter a valid email", "Email is required"),
  password: newPassword(),
});
type Values = z.infer<typeof schema>;
export const Route = createFileRoute("/auth/guest-signup")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  staticData: { title: "Join shared meeting" },
});

function RouteComponent() {
  const { email } = Route.useSearch();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { full_name: "", email: email ?? "", password: "" },
  });
  const signup = useMutation({
    mutationFn: (values: Values) =>
      ky
        .post("api/v1/register", {
          baseUrl: env.VITE_AUTH_API_URL,
          json: values,
        })
        .json<{ message: string }>(),
    onSuccess(data) {
      form.setError("root.serverError", { message: data.message });
    },
    onError(error: HTTPError) {
      const detail =
        error.data && typeof error.data === "object" && "detail" in error.data
          ? error.data.detail
          : null;
      const fields = parseBackendError(detail);
      if (fields) {
        fields.forEach(([field, message]) =>
          form.setError(field as keyof Values, { message }),
        );
        return;
      }
      const refusal = parseRefusal(detail);
      if (refusal?.code === "NO_INVITATION") {
        form.setError("email", {
          message: "We have no meeting invitation for this address.",
        });
        return;
      }
      form.setError("root.serverError", {
        message:
          typeof detail === "string"
            ? detail
            : (refusal?.message ?? "We could not create your account."),
      });
    },
  });
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-6 px-6">
      <div>
        <h1 className="text-3xl font-semibold">Join your shared meeting</h1>
        <p className="mt-2 text-muted-foreground">
          Create an account for the meeting invitation sent to you.
        </p>
      </div>
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((values) => signup.mutate(values))}
        >
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full name</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" autoComplete="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} autoComplete="new-password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.formState.errors.root?.serverError && (
            <p className="text-sm text-destructive">
              {form.formState.errors.root.serverError.message}
            </p>
          )}
          <Button type="submit" disabled={signup.isPending}>
            {signup.isPending ? "Creating account..." : "Create guest account"}
          </Button>
        </form>
      </Form>
      <p className="text-sm">
        Need a company workspace?{" "}
        <Link to="/auth/signup" className="text-[#c11574] hover:underline">
          Create one here
        </Link>
        .
      </p>
    </div>
  );
}
